import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  ExperienceActionRequestBodyType,
  ExperienceActionRequestPathParamsType,
  ExperienceActionResponseBodyType,
} from "@intelligems/ig-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { EXPERIMENT_ROUTES } from "../../routes/routes";
import { mpEndExperience } from "../../utils/mixpanel";
import { useConfiguration } from "../configurations/use-configuration";
import { experienceKeys, historyKeys } from "../queryKeys";
import { ApiRequest } from "../types/api";

import { experienceAction } from "./experience-action";

export const useEndExperience = (
  noRedirect?: boolean,
  experienceId?: string | undefined
) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const shouldDuplicateProducts = useConfiguration<boolean>(
    "should_duplicate_products"
  );
  return useMutation<
    ExperienceActionResponseBodyType,
    Error,
    ApiRequest<
      ExperienceActionRequestBodyType,
      ExperienceActionRequestPathParamsType & {
        name: string;
      }
    >
  >({
    mutationKey: ["endExperience"],
    mutationFn: async (data) => experienceAction(data),
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 503) {
          toast.info("Ending experience...");
        } else {
          toast.error("Error ending experience");
        }
      }
    },
    onSuccess: async (data, payload) => {
      await queryClient.invalidateQueries({
        queryKey:
          data.experience?.category === "experiment"
            ? experienceKeys.experimentList
            : experienceKeys.personalizationList,
      });

      if (experienceId) {
        // invalidating instead of setting bc return is experience array
        await queryClient.invalidateQueries({
          queryKey: experienceKeys.one(experienceId),
        });
        void queryClient.invalidateQueries({
          queryKey: historyKeys.one(experienceId),
        });
      }
      if (data.experience?.category === "experiment") {
        mpEndExperience({
          id: payload.pathParams.experienceId,
          name: payload.pathParams.name,
        });
      }

      toast.success("Experience Successfully Ended!");

      if (
        shouldDuplicateProducts?.value &&
        data.experience?.testTypes?.hasTestPricing
      ) {
        navigate(
          `${EXPERIMENT_ROUTES.DuplicateProducts}/${payload.pathParams.experienceId}`,
          {
            state: {
              action: "end",
            },
          }
        );
      } else {
        if (data.experience?.category === "experiment") {
          !noRedirect && navigate(EXPERIMENT_ROUTES.Experiences);
        }
      }
    },
  });
};
